import React from 'react';
import { useQuery } from 'react-query';
import {
  Users,
  Gamepad2,
  Activity,
  DollarSign,
} from 'lucide-react';
import { api, endpoints } from '@/services/api';
import { DashboardStats } from '@/types';
import StatsCard from '@/components/common/StatsCard';

// Demo data for when backend is not available
const DEMO_STATS: DashboardStats = {
  users: {
    total: 1248,
    active: 342,
    newToday: 23,
    newThisWeek: 156,
    growthRate: '12.5',
  },
  financial: {
    totalVolume: 2847650,
    totalTransactions: 4521,
    todayTransactions: [
      { _id: 'deposit', count: 45, amount: 125400 },
      { _id: 'withdrawal', count: 28, amount: 89200 },
      { _id: 'bet', count: 312, amount: 456800 },
    ],
  },
  gaming: {
    totalBets: 8934,
    totalBetAmount: 1245600,
    totalWinnings: 1089450,
    houseEdge: '12.5',
  },
};

const Dashboard: React.FC = () => {
  // Check if using demo token
  const isDemoMode = localStorage.getItem('adminToken')?.startsWith('demo_admin_token_');

  // Fetch dashboard statistics - single API call
  const { data: response, isLoading: statsLoading } = useQuery<{ data: DashboardStats }>(
    'dashboard-stats',
    () => api.get(endpoints.admin.dashboard),
    {
      enabled: !isDemoMode, // Skip API call in demo mode
      refetchInterval: isDemoMode ? false : 60000, // Refresh every 60 seconds (reduced from 30s)
      staleTime: 30000, // Consider data fresh for 30 seconds
    }
  );

  // Use demo data in demo mode, otherwise use API response
  const stats = isDemoMode ? DEMO_STATS : response?.data;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Dashboard</h1>
        <p className="text-slate-600">
          Welcome back! Here's an overview of your InCoinX platform.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Users"
          value={formatNumber(stats?.users?.total || 0)}
          change={{
            value: parseFloat(stats?.users?.growthRate || '0'),
            type: 'increase',
            period: 'from last week',
          }}
          icon={<Users className="w-6 h-6" />}
          color="blue"
          loading={statsLoading}
        />

        <StatsCard
          title="Active Users"
          value={formatNumber(stats?.users?.active || 0)}
          change={{
            value: 5.2,
            type: 'increase',
            period: 'from last week',
          }}
          icon={<Activity className="w-6 h-6" />}
          color="green"
          loading={statsLoading}
        />

        <StatsCard
          title="Total Volume"
          value={formatCurrency(stats?.financial?.totalVolume || 0)}
          change={{
            value: 8.2,
            type: 'increase',
            period: 'from last week',
          }}
          icon={<DollarSign className="w-6 h-6" />}
          color="purple"
          loading={statsLoading}
        />

        <StatsCard
          title="Total Bets"
          value={formatNumber(stats?.gaming?.totalBets || 0)}
          change={{
            value: 12.5,
            type: 'increase',
            period: 'from last week',
          }}
          icon={<Gamepad2 className="w-6 h-6" />}
          color="yellow"
          loading={statsLoading}
        />
      </div>

      {/* Overview Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span>User Statistics</span>
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">Total Users</p>
                <p className="text-xs text-slate-500">All registered users</p>
              </div>
              <span className="text-lg font-semibold text-slate-900">
                {formatNumber(stats?.users?.total || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">Active Users</p>
                <p className="text-xs text-slate-500">Currently active</p>
              </div>
              <span className="text-lg font-semibold text-green-600">
                {formatNumber(stats?.users?.active || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">New Today</p>
                <p className="text-xs text-slate-500">Registered today</p>
              </div>
              <span className="text-lg font-semibold text-blue-600">
                {formatNumber(stats?.users?.newToday || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">New This Week</p>
                <p className="text-xs text-slate-500">Last 7 days</p>
              </div>
              <span className="text-lg font-semibold text-purple-600">
                {formatNumber(stats?.users?.newThisWeek || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Financial Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span>Financial Overview</span>
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">Total Volume</p>
                <p className="text-xs text-slate-500">All transactions</p>
              </div>
              <span className="text-lg font-semibold text-slate-900">
                {formatCurrency(stats?.financial?.totalVolume || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">Total Transactions</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
              <span className="text-lg font-semibold text-green-600">
                {formatNumber(stats?.financial?.totalTransactions || 0)}
              </span>
            </div>

            <div className="py-3">
              <p className="text-sm font-medium text-slate-900 mb-3">Today's Transactions</p>
              <div className="space-y-2">
                {stats?.financial?.todayTransactions && stats.financial.todayTransactions.length > 0 ? (
                  stats.financial.todayTransactions.map((txn: any) => (
                    <div key={txn._id} className="flex justify-between text-xs">
                      <span className="text-slate-600 capitalize">{txn._id}</span>
                      <span className="font-medium text-slate-900">
                        {txn.count} ({formatCurrency(txn.amount)})
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400">No transactions today</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Gaming Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
            <Gamepad2 className="w-5 h-5 text-purple-600" />
            <span>Gaming Statistics</span>
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">Total Bets</p>
                <p className="text-xs text-slate-500">All time</p>
              </div>
              <span className="text-lg font-semibold text-slate-900">
                {formatNumber(stats?.gaming?.totalBets || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">Total Bet Amount</p>
                <p className="text-xs text-slate-500">Total wagered</p>
              </div>
              <span className="text-lg font-semibold text-purple-600">
                {formatCurrency(stats?.gaming?.totalBetAmount || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">Total Winnings</p>
                <p className="text-xs text-slate-500">Paid to users</p>
              </div>
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(stats?.gaming?.totalWinnings || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">House Edge</p>
                <p className="text-xs text-slate-500">Platform profit</p>
              </div>
              <span className="text-lg font-semibold text-blue-600">
                {stats?.gaming?.houseEdge || 0}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Footer */}
      <div className={`mt-8 border rounded-xl p-4 ${
        isDemoMode
          ? 'bg-purple-50 border-purple-200'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-start space-x-3">
          <Activity className={`w-5 h-5 mt-0.5 ${
            isDemoMode ? 'text-purple-600' : 'text-blue-600'
          }`} />
          <div>
            <p className={`text-sm font-medium ${
              isDemoMode ? 'text-purple-900' : 'text-blue-900'
            }`}>
              {isDemoMode ? 'Demo Mode' : 'Dashboard Auto-Refresh'}
            </p>
            <p className={`text-xs mt-1 ${
              isDemoMode ? 'text-purple-700' : 'text-blue-700'
            }`}>
              {isDemoMode
                ? 'You are viewing demo data. Login with real credentials to see live statistics.'
                : 'Statistics are automatically refreshed every 60 seconds to show the latest data.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
